import React, { useRef, useState, useEffect } from 'react'
import {
  IAppState as AppState,
  init,
  AppConfigService,
  initGTM,
  initialAppState,
  setHeadersDetails,
  customStorageUtil,
  setGrowthBook,
  GrowthBookUtils,
  HeadTabActions,
  setFlagShip,
  GROWTHBOOK_FLAGS,
  HelpersUtil,
  IAppState
} from '@mafc/common'
import { Store, AnyAction, ReducersMapObject } from 'redux'
import MD5 from 'crypto-js/md5'
import Base64 from 'crypto-js/enc-base64'
import { Provider } from 'react-redux'
import { isServer, setGrowthBookFlagsInRedux } from '../utils'
import Cookies from 'universal-cookie'
import SSRCookies from 'cookies'
import LayoutReducer, {
  LayoutInitialState
} from '../components/Layout/redux/reducers'
import initializeBraze from './initializeBraze'
import * as storeIds from './storeIds'

import { CountryConfig } from './storeIds'

import { getOrCreateStore } from './getStore'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import { startFlagshipSDK } from './startFlagshipSDK'
import FlagShipWrapper from './flagShipWrapper'
const { Flagship } = require('@flagship.io/react-sdk')

const __COOKIE_BANNER__: string = 'maf-cookie-banner-accepted'

export interface ReduxWrapper {
  initialState: any
  initialProps: any
  resources?: (appConfig: any) => { en: any; ar: any } | any
  pageProps?: any
  theme?: any
  withLayout?: boolean
}

const STORE_INFO = 'storeInfo'

type ComponentType<T = {}> =
  | ({
      getInitialProps?: (context: any) => void
    } & React.ComponentType<T>)
  | React.NamedExoticComponent<T>

export interface WrapperType<P> extends React.FunctionComponent<P> {
  getInitialProps?: any
  getServerSideProps?: any
  getStaticProps?: any
}

const getStoreFromUrl = (url: string) => {
  let storeId = ''
  let language = ''
  if (url) {
    const [, urlStoreId = '', urlLanguage = ''] = url.split('/')
    let tempLanguage = urlLanguage

    if (tempLanguage.includes('?')) {
      tempLanguage = tempLanguage.split('?')[0]
    }
    if (urlStoreId.startsWith('maf')) {
      storeId = urlStoreId
    }
    if (tempLanguage === 'en' || tempLanguage === 'ar') {
      language = tempLanguage
    }
  }
  return [storeId, language]
}

const extractStoreConfig = (
  cookies: Cookies | SSRCookies,
  areaCodekey: string
) => {
  return {
    userId: cookies.get('maf-user-id') || '',
    accessToken: cookies.get('guest-session-token') || '',
    areaCode: cookies.get(areaCodekey) || '',
    latitude: cookies.get('lat') || '',
    longitude: cookies.get('long') || ''
  }
}

export const getUserCredentials = (cookies: Cookies | SSRCookies) => {
  if (!cookies) return {}

  return {
    userId: cookies.get('userId'),
    token: cookies.get('reactUserToken'),
    authToken: cookies.get('authToken')
  }
}

interface Params {
  storeInfo?: string
  url?: string
  ssrLanguage?: string
  cookieStr?: string
}

const getCountryConfig = (storeId: string, lang: string) => {
  let extraConfig = {} as CountryConfig
  try {
    extraConfig = storeIds[storeId] || {}
  } catch (error) {
    // Do nothing
  }
  // Adding country based config
  const countryBasedConfig = {
    ...extraConfig,
    locale: `${lang}-${extraConfig.code}`,
    country: storeId.substr(3)
  }
  return countryBasedConfig
}

const configureApigeeFeatures = (
  countryBasedConfig: any,
  countryConfiguration: any,
  state: IAppState
) => {
  const features = [
    { featureKey: 'isLocationCXEnable', feature: 'MAP_LOCATION_CX' },
    { featureKey: 'isVerifyOTPRequired', feature: 'OTP_REQUIRED' },
    { featureKey: 'isPromoBannerEnabled', feature: 'PDP_SHOW_BANNERS' },
    {
      featureKey: 'isSponsoredFbtEnabled',
      feature: 'PDP_SPONSORED_BASKET_SHOPPER'
    },
    { featureKey: 'nowDescription', feature: 'carrefour_now_info_description' },
    {
      featureKey: 'isSponsoredProductCarouselEnabled',
      feature: 'PLP_SPONSORED_PRODUCTS_CAROUSEL'
    },
    {
      featureKey: 'isSponsoredCrissCrossEnabled',
      feature: 'PLP_SPONSORED_CRISS_CROSS'
    },
    {
      featureKey: 'isForcePhoneNumberValidation',
      feature: 'FORCE_PHONE_NUMBER_VALIDATION'
    },
    { featureKey: 'isOTPCallEnabled', feature: 'PHONE_OTP' },
    {
      featureKey: 'isSponsoredOrderReportingEnabled',
      feature: 'SPONSORED_DIRECT_ORDER_REPORTING'
    },
    {
      featureKey: 'isSponsoredDisclaimerMessage',
      feature: 'DISCLAIMER_MESSAGE'
    },
    {
      featureKey: 'isBambuserMobileAppDisabled',
      feature: 'BAMBUSER_MOBILE_APP_DISABLED'
    },
    {
      featureKey: 'isCarrefourPaySupported',
      feature: 'CARREFOUR_PAY'
    },
    {
      featureKey: 'isCitrusBannerXEnabled',
      feature: 'CLP_SPONSORED_BANNERX'
    },
    {
      featureKey: 'isWhatsAppOtpEnabled',
      feature: 'WHATSAPP_OTP'
    },
    {
      featureKey: 'isExistingNumberRegistrationEnabled',
      feature: 'EXISTING_NUMBER_REGISTRATION'
    },
    {
      featureKey: 'isAccidentalDamageEnabled',
      feature: 'PDP_ACCIDENTAL_DAMAGE'
    },
    {
      featureKey: 'isFoodNutritionEnabled',
      feature: 'PDP_FOOD_NUTRITION'
    },
    {
      featureKey: 'isProductVariantsEnabled',
      feature: 'PRODUCT_VARIANTS'
    }
  ]
  features.forEach((featureData: { featureKey: string; feature: string }) => {
    countryBasedConfig.features[featureData.featureKey] =
      GrowthBookUtils.getGrowthBookFeatureValueViaInstance(
        state,
        featureData.feature.toLowerCase()
      )?.on || false
  })
  countryBasedConfig.latitude =
    countryConfiguration?.defaultLatitude || countryBasedConfig.latitude
  countryBasedConfig.longitude =
    countryConfiguration?.defaultLongitude || countryBasedConfig.longitude
  countryBasedConfig.defaultAreaCode =
    countryConfiguration?.defaultAreaCode || countryBasedConfig.defaultAreaCode
  countryBasedConfig.phoneCountryCode =
    countryConfiguration?.phoneCountryCode || ''
  countryBasedConfig.currencyCodeLocalized =
    countryConfiguration?.currencyCodeLocalized || ''
  countryBasedConfig.currencyDecimal =
    Number(countryConfiguration?.currencyDecimal) || 2
  countryBasedConfig.gamConfigDetails = countryConfiguration?.gamConfig
  countryBasedConfig.customerCareNumber =
    countryConfiguration?.customerCareNumber || ''
  countryBasedConfig.currencyRate = countryConfiguration?.currencyRate || ''
  /* eslint-disable camelcase */
  countryBasedConfig.disclaimerMessage =
    countryConfiguration?.disclaimer_message || ''

  countryBasedConfig.features.isCheckoutDotComSavedCardSupported =
    countryConfiguration?.paymentMethods?.isCheckoutDotComSavedCardSupported
  countryBasedConfig.features.isCheckoutDotComSupported =
    countryConfiguration?.paymentMethods?.isCheckoutDotComSupported
  countryBasedConfig.nowInfoDescription =
    countryConfiguration?.remoteLocalization?.carrefour_now_info_description ||
    ''
  // countryBasedConfig.sfbcMessage = countryConfiguration?.sfbc_message || ''
  // countryBasedConfig.fbcMessage = countryConfiguration?.fbc_message || ''
  countryBasedConfig.fbcMessage = countryConfiguration?.fbc || {}
  countryBasedConfig.serviceWarrantyId =
    countryConfiguration?.serviceWarrantyId || ''
  return countryBasedConfig
}

const connectSession = async (
  cookies: Cookies | SSRCookies,
  store: Store,
  params: Params = {}
) => {
  const { ssrLanguage, storeInfo: storeInfoParam = '', url = '' } = params
  const [, languageUrl] = getStoreFromUrl(url)
  const setCookie = (key: string, value: any, expires: number) => {
    try {
      if (value) {
        cookies.set(key, value, {
          secure: false,
          httpOnly: false,
          maxAge: expires,
          path: '/',
          encode: (value) => value
        })
      } else {
        cookies.set(key, '', {
          secure: false,
          httpOnly: false,
          maxAge: expires,
          path: '/',
          encode: (value) => value
        })
      }
    } catch (err) {
      console.error('Handled error', err)
    }
  }
  const storeInfo = decodeURIComponent(storeInfoParam) || ''
  // Anything returned here can be access by the client
  let [storeId, language, currency] = storeInfo ? storeInfo.split('|') : []
  const queryLang =
    ssrLanguage === 'en' || ssrLanguage === 'ar' ? ssrLanguage : ''
  const lang = languageUrl || queryLang || language
  currency = (currency || '').substr(0, 3)
  const areaCodekey = `${storeId}-preferred-delivery-area`
  const prevAreaCode = cookies.get('prevAreaCode')
  let countryBasedConfig = getCountryConfig(storeId, lang)
  countryBasedConfig.defaultCountryLat = countryBasedConfig.latitude
  countryBasedConfig.defaultCountryLng = countryBasedConfig.longitude
  // get user credential from cookies
  const userCredentials = getUserCredentials(cookies)
  // extract config from cookies
  const config = extractStoreConfig(cookies, areaCodekey)
  const userToken = userCredentials.token || userCredentials.authToken
  const firstTimeloggin = userToken ? userToken !== config.accessToken : false

  // Growthbook set state
  let mafSessionId = cookies.get('maf-session-id')
  const {
    growthBook: { attributes: _attributes = {} } = {}
  } = store.getState()?.appConfig
  if (isServer() || !Object.keys(_attributes).length) {
    const attributes = {
      id: mafSessionId || 'default',
      platform: 'Web',
      country: storeId,
      username: userCredentials?.userId || '',
      language: lang,
      preview: !!cookies.get('gb-preview'),
      loggedIn: !!userToken,
      customerId: cookies.get('customerId') || ''
    }

    const {
      features = {}
    } = (await AppConfigService.getFeatureDetails()) as any
    let parsedFeatures = features

    try {
      const regex = /hypermax/gi
      const replacedFeatures = JSON.stringify(features).replaceAll(regex, '')
      parsedFeatures = JSON.parse(replacedFeatures)
    } catch (e) {
      console.error('Error in parsing features', e)
    }

    await store.dispatch(
      setGrowthBook({ features: parsedFeatures, attributes })
    )
  }
  setGrowthBookFlagsInRedux(store)
  /** Country Config Default Settings */

  countryBasedConfig.selectedEnv =
    (process.env.REACT_APP_ENV !== 'prod' && cookies.get('env')) || ''
  const countryConfigResponse = await AppConfigService.getAppCountryConfig({
    language: lang,
    StoreId: storeId,
    Currency: currency,
    headers: {
      token: ''
    }
  })
  const {
    countryConfiguration,
    unSupportedFeatures,
    supportedLanguages,
    sharedConfig
  } = countryConfigResponse?.data
  if (countryConfigResponse?.meta?.statusCode === 200) {
    countryBasedConfig = configureApigeeFeatures(
      countryBasedConfig,
      countryConfiguration,
      store.getState()
    )
    countryBasedConfig.countryConfiguration = countryConfiguration
    countryBasedConfig.sharedConfig = sharedConfig
    countryBasedConfig.unSupportedFeatures = unSupportedFeatures
    countryBasedConfig.supportedLanguages = supportedLanguages || []
  } else {
    console.error(' ERROR IN Country Config Response ', countryConfigResponse)
  }
  const expires = 260000000
  try {
    const headerResponse = await AppConfigService.getHeaderDetails({
      storeId,
      headers: {
        lang,
        storeId,
        langCode: lang,
        userId: userCredentials?.userId || 'anonymous'
      }
    })
    await store.dispatch(setHeadersDetails(headerResponse))
  } catch (error) {
    console.error(error)
  }

  const {
    features,
    latitude: defaultLat,
    longitude: defaultLong,
    defaultAreaCode
  } = countryBasedConfig

  const isLocationCXEnable = features.isLocationCXEnable
  const currentAreaCode = config.areaCode || defaultAreaCode
  const selectNewLocation = !!(
    isLocationCXEnable &&
    !config.latitude &&
    !config.longitude &&
    userToken
  )
  const getPhoneExpression = () => {
    const currentStoreId = countryConfiguration?.storeId
    const phoneNumberInfo = countryConfigResponse?.data?.sharedConfig?.countryCodePhoneNumber.find(
      (storeId: any) => storeId.storeId === currentStoreId
    )
    return phoneNumberInfo?.expression
  }

  let { latitude, longitude } = config
  latitude = latitude || defaultLat
  longitude = longitude || defaultLong
  if (!mafSessionId) {
    const sessionId = HelpersUtil.generateUuid()
    setCookie('maf-session-id', sessionId, expires)
    mafSessionId = sessionId
  }
  let hashedUserId = ''
  if (userCredentials?.userId) {
    const hash = MD5(userCredentials.userId.toLowerCase())
    hashedUserId = Base64.stringify(hash)
    setCookie('maf-user-id', hashedUserId, expires)
  }
  if (!currentAreaCode || prevAreaCode !== currentAreaCode || firstTimeloggin) {
    await store.dispatch(
      init({
        ...countryBasedConfig,
        storeId,
        language: lang,
        currency,
        userCredentials,
        latitude,
        longitude,
        selectNewLocation,
        phoneExpression: getPhoneExpression(),
        areaCode: currentAreaCode,
        sessionId: mafSessionId,
        mafUserId: hashedUserId
      })
    )
    setCookie('storeInfo', `${storeId}|${lang}|${currency}`, 2600000000)
    setCookie(areaCodekey, currentAreaCode, expires)
    setCookie('prevAreaCode', currentAreaCode, expires)
  } else {
    setCookie('storeInfo', `${storeId}|${lang}|${currency}`, 2600000000)
    await store.dispatch(
      init({
        ...config,
        storeId,
        language: lang,
        currency,
        ...countryBasedConfig,
        userCredentials,
        latitude,
        longitude,
        selectNewLocation,
        phoneExpression: getPhoneExpression(),
        sessionId: mafSessionId,
        mafUserId: hashedUserId
      })
    )
  }

  const isPolygonChange = GrowthBookUtils.getGrowthBookFeatureValueViaInstance(
    store?.getState(),
    'is_polygon_cached'
  )?.value
  if (isPolygonChange) {
    try {
      await HeadTabActions.getDeliveryTime()(
        store.dispatch,
        store.getState(),
        cookies,
        isPolygonChange
      )
    } catch (error) {
      console.log('Delivery Time API Failed inside with store', error)
    }
  }
}

const establishSession = async (
  { req, res, query }: any,
  store: Store,
  cookies: Cookies | SSRCookies
) => {
  if (req) {
    const { language: ssrLanguage } = query
    const { headers, url } = req as any
    const [storeUrl, langUrl] = getStoreFromUrl(url)
    // @ts-ignore
    const [storeCookie, langCookie, currCookie] = (
      cookies.get(STORE_INFO) || ''
    ).split('|')
    const [storeH, langH, currH] = (
      headers[STORE_INFO.toLowerCase()] || ''
    ).split('|')
    let storeInfo = `${storeUrl || storeCookie || storeH}|${
      langUrl || langCookie || langH
    }|${currCookie || currH}`
    storeInfo = decodeURIComponent(storeInfo) || ''
    // Anything returned here can be access by the client
    const [storeId, language] = storeInfo ? storeInfo.split('|') : []
    const lang = langUrl || ssrLanguage || language
    if (!storeId || storeId === 'undefined' || language === 'undefined') {
      // IN CASE OF HEALTH CHECK API TRYING TO ACCESS AND WE DON'T KNOW THE STORE
      res.statusCode = 200
      return
    }
    return await connectSession(cookies, store, {
      storeInfo,
      url,
      ssrLanguage: lang,
      cookieStr: req.headers.cookie
    })
  }
}

const establishClientSession = async (store: Store) => {
  const cookies = new Cookies()
  const url = window.location.href
  const storeInfo = cookies.get('storeInfo')
  return await connectSession(cookies, store, {
    storeInfo,
    url,
    cookieStr: document.cookie
  })
}

export const withWrapper = (
  WrapperComponent: ComponentType<{
    appConfig: any
    resources: any
    baseTheme: any
    withLayout?: boolean
  }>,
  isSSR = true
) => {
  const withReduxWrapper = (
    Component: ComponentType<{ appConfig: any }>,
    reducers: ReducersMapObject = {},
    initialState: any = {}
  ) => {
    const displayName = `withAppShell(${
      Component.displayName || Component.name || 'Component'
    })`
    /**
     * SSR BASED REDUX INITIALIZATION FOR NEXT JS
     */
    initialState = { ...initialState, layout: LayoutInitialState }
    reducers = { ...reducers, layout: LayoutReducer }
    /**
     *  Initilize props for SSR based apps
     */
    const initProps = async (
      context: any,
      callback: any
    ): Promise<{ initialProps: any; initialState: any }> => {
      let initialProps = {} as any
      const { req, res } = context.ctx
      let store: Store

      if (!req) {
        // on back button of browser, https://github.com/vercel/next.js/issues/9833
        store = getOrCreateStore(initialState, reducers, context)
      } else {
        const cookies = new SSRCookies(req, res)
        const cookieVisibility = cookies.get(__COOKIE_BANNER__) !== 'true'
        store = getOrCreateStore(
          { ...initialState, cookieBanner: { cookieVisibility } },
          reducers,
          context
        )
        await establishSession(context.ctx, store, cookies)
        const isFLagshipEnabled = GrowthBookUtils.getGrowthBookFeatureValueViaInstance(
          store?.getState(),
          GROWTHBOOK_FLAGS.flagshipEnabled
        )?.on
        if (isFLagshipEnabled) {
          startFlagshipSDK()
          const {
            visitor: newVisitor,
            initialVisitorData: newVisitorData
          } = await establishFlagship(store.getState().appConfig)
          store.dispatch(
            setFlagShip({
              initialVisitorData: newVisitorData,
              initialFlagsData: newVisitor.getFlagsDataArray()
            })
          )
        }
      }
      if (callback) {
        const appConfig = store.getState().appConfig
        // @ts-ignore
        initialProps = await callback(
          // merging store into context instead of just passing as another argument because it's impossible to override getInitialProps signature
          context.ctx
            ? {
                ...context,
                ctx: { ...(context as any).ctx, store, appConfig }
              }
            : { ...context, store, appConfig }
        )
        const {
          pageProps: { isSecure }
        } = initialProps || { pageProps: { isSecure: false } }
        // Validation on secure route, if not authorized it will redirect to login page
        if (isSecure) {
          const { storeId, userCredentials, language } = appConfig
          const token = userCredentials?.token || userCredentials?.authToken
          if (!token) {
            res.writeHead(302, {
              Location: `/${storeId}/${language}/v1/login?redirect=${escape(
                req.url
              )}`
            })
            res.end()
          }
        }
      }
      const state = store.getState()

      return {
        initialProps,
        initialState: { ...state }
      }
    }
    const establishFlagship = async (appConfig) => {
      const {
        growthBook: { attributes },
        areaCode = '',
        country = '',
        storeId = '',
        currencyCodeLocalized: currency = '',
        sessionId: mafSessionId = '',
        mafUserId = ''
      } = appConfig
      const visitorId = mafUserId || mafSessionId

      const initialVisitorData = {
        id: visitorId,
        context: {
          ...attributes,
          areaCode,
          country,
          storeId,
          currency,
          id: visitorId
        }
      }

      const visitor = Flagship.newVisitor({
        visitorId: initialVisitorData.id,
        context: initialVisitorData.context,
        anonymousId: mafUserId ? mafSessionId : '',
        isAuthenticated: !!mafUserId
      })

      await visitor.fetchFlags()
      return { visitor, initialVisitorData }
    }
    const getInitialAppProps = <P extends {} = any>(
      callback?: (
        context: any & { store: Store<AppState, AnyAction> }
      ) => P | void
    ) => async (context: any) => (await initProps(context, callback)) as any // this is just to convince TS

    const getInitialPageProps = <P extends {} = any>(
      callback?: (
        context: any & { store: Store<AppState, AnyAction> }
      ) => P | void
    ) => async (context: any) => {
      if (context.store && callback) {
        console.warn('No need to wrap pages if _app was wrapped')
        return callback(context as any)
      }
      return await initProps(context, callback)
    }

    const Wrapper: WrapperType<ReduxWrapper> = (
      {
        initialState: initialStateFromProps = {},
        initialProps: initialPropsFromProps = {},
        ...rest
      },
      appContext
    ) => {
      let initialFlagsData, initialVisitorData
      if (!isSSR) {
        // For client side application, NEED TO BE REMOVED AFTER DATASTORE IS INTRODUCED
        const cookies = new Cookies()

        const [storeId, language] = (cookies.get('storeInfo') || '').split('|')
        if (storeId) {
          const countryBasedConfig = getCountryConfig(storeId, language)
          const { latitude, longitude } = initialAppState.appConfig
          initialState = {
            ...initialState,
            ...initialAppState,
            appConfig: {
              ...countryBasedConfig,
              ...initialAppState.appConfig,
              latitude: latitude || countryBasedConfig.latitude, // set to default if no lat long exist
              longitude: longitude || countryBasedConfig.longitude,
              features: countryBasedConfig.features
            }
          }
        }
      }

      const jointState = { ...initialState, ...initialStateFromProps }
      const store = useRef<Store>(
        getOrCreateStore(jointState, reducers, appContext)
      )

      const appConfig = store.current.getState().appConfig

      if (initialPropsFromProps && initialPropsFromProps.pageProps) {
        rest.pageProps = {
          ...initialPropsFromProps.pageProps, // this comes from wrapper in _app mode
          ...rest.pageProps // this comes from gssp/gsp in _app mode
        }
        const {
          initialFlagsData: flagsData = [],
          initialVisitorData: visitorData = {}
        } = initialStateFromProps?.appConfig?.flagShip
        initialVisitorData = visitorData
        initialFlagsData = flagsData
      }
      const resultProps = rest
      const { resources, theme, pageProps } = resultProps
      const { withLayout } = pageProps || {}
      let resource = resources
      if (typeof resources === 'function') {
        resource = resources(appConfig)
      }

      if (!isServer()) {
        const { initStorageManager } = customStorageUtil
        const storage = initStorageManager(() => localStorage)
        const parsed = storage.getItem('initBrazeAnonymousUserWithData')
        const userRegistered = storage.getItem('userRegistered')
        const userLoggedIn = storage.getItem('userLoggedIn')

        if (
          parsed ||
          appConfig?.userCredentials?.userId ||
          userLoggedIn ||
          userRegistered
        ) {
          import('@braze/web-sdk').then(({ default: appboy }) =>
            initializeBraze(appboy, store, appConfig, parsed)
          )
        }
      }

      if (!isServer() && !appConfig.gtmInitialized) {
        const storeId =
          appConfig?.sharedConfig?.unified_gtm === true ||
          (window as any).gtmUnifiedFlag === true
            ? 'unified'
            : appConfig.storeId
        initGTM(storeId)
      }

      const initializeGrowthBook = () => {
        const {
          growthBook: { features = {}, attributes = {} }
        } = store.current.getState()?.appConfig
        return GrowthBookUtils.createGrowthBookInstance(
          { features, attributes },
          store.current.getState()
        )
      }

      const [growthBookUpdated, setGrowthBookUpdated]: [any, any] = useState(
        initializeGrowthBook
      )

      // only for client side, remove this code when all pages switched to ssr
      // api's get called in parallel
      // we have to wait for switching country api response
      // then render the component
      const [isEstablishClient, updateEstablishClient] = useState(isSSR)
      useEffect(() => {
        if (!isEstablishClient) {
          establishClientSession(store.current).finally(() => {
            const growthbook = initializeGrowthBook()
            setGrowthBookUpdated(growthbook)
            updateEstablishClient(true)
          })
        }
      }, [isEstablishClient])

      if (!isEstablishClient) {
        return null
      }

      return (
        <Provider store={store.current}>
          <GrowthBookProvider growthbook={growthBookUpdated}>
            <FlagShipWrapper
              initialVisitorData={initialVisitorData}
              initialFlagsData={initialFlagsData}
              isSSR={isSSR}
            >
              {/* @ts-ignore */}
              <WrapperComponent
                appConfig={appConfig}
                baseTheme={theme}
                resources={resource}
                withLayout={withLayout}
              >
                {/* @ts-ignore */}
                <Component
                  {...initialPropsFromProps}
                  {...resultProps}
                  appConfig={appConfig}
                />
              </WrapperComponent>
            </FlagShipWrapper>
          </GrowthBookProvider>
        </Provider>
      )
    }

    Wrapper.displayName = displayName

    if (Component && 'getInitialProps' in Component) {
      Wrapper.getInitialProps = (context: any) => {
        const callback = Component.getInitialProps // bind?
        return (context.ctx
          ? getInitialAppProps(callback)
          : getInitialPageProps(callback))(context)
      }
    }
    return Wrapper
  }

  return {
    withReduxWrapper
  }
}
